import React, { useState, useEffect, forwardRef } from "react";
import {
  Box,
  TextField,
  Button,
  MenuItem,
  Typography,
  Alert,
  Snackbar,
  Divider,
  useTheme,
  Slide,
  CircularProgress,
  Portal,
  Fade,
} from "@mui/material";
import { tokens } from "../../theme";
import { logInstallmentPayment } from "../../services/PaymentService";

const SlideTransition = forwardRef(function Transition(props, ref) {
  return <Slide direction="left" ref={ref} {...props} />;
});

const LogPayments = ({
  salesId,
  remainingBalance = null,
  onPaymentLogged,
  onClose,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("CASH");
  const [note, setNote] = useState("");
  const [receiptNumber, setReceiptNumber] = useState("");
  const [nextDueDate, setNextDueDate] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  useEffect(() => {
    setAmount("");
    setMethod("CASH");
    setNote("");
    setReceiptNumber("");
    setNextDueDate(null);
    setError(null);
    setSuccessMsg(null);
  }, [salesId]);

  const handleCloseSnackbar = (_, reason) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  const validate = () => {
    setError(null);
    const num = Number(amount || 0);
    if (!salesId) return "Missing sales id";
    if (isNaN(num) || num <= 0) return "Amount must be greater than 0";
    if (remainingBalance != null && num > Number(remainingBalance)) {
      return "Amount must be less than or equal to outstanding balance";
    }
    return null;
  };

  const inputStyleProps = {
    "& .MuiFilledInput-root": {
      backgroundColor:
        theme.palette.mode === "dark"
          ? "rgba(255,255,255,0.05)"
          : "rgba(0,0,0,0.04)",
      borderRadius: "8px",
      border: "1px solid transparent",
      transition: "all 0.2s ease",
      "&:hover": {
        backgroundColor:
          theme.palette.mode === "dark"
            ? "rgba(255,255,255,0.08)"
            : "rgba(0,0,0,0.06)",
      },
      "&.Mui-focused": {
        backgroundColor:
          theme.palette.mode === "dark"
            ? "rgba(255,255,255,0.08)"
            : "rgba(0,0,0,0.06)",
        borderColor: colors.blueAccent[500],
      },
    },
    "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button":
      {
        display: "none",
        WebkitAppearance: "none",
        margin: 0,
      },
    "& input[type=number]": {
      MozAppearance: "textfield",
    },
  };

  const dropdownPaperStyle = {
    backgroundColor:
      theme.palette.mode === "dark"
        ? `rgba(${parseInt(colors.primary[400].slice(1, 3), 16)}, ${parseInt(colors.primary[400].slice(3, 5), 16)}, ${parseInt(colors.primary[400].slice(5, 7), 16)}, 0.8)`
        : "rgba(255, 255, 255, 0.95)",
    backdropFilter: "blur(16px)",
    border: `1px solid ${
      theme.palette.mode === "dark"
        ? "rgba(255, 255, 255, 0.15)"
        : "rgba(0, 0, 0, 0.1)"
    }`,
    boxShadow:
      theme.palette.mode === "dark"
        ? "0 8px 32px 0 rgba(0, 0, 0, 0.4)"
        : "0 8px 32px 0 rgba(0, 0, 0, 0.15)",
    borderRadius: "12px",
    mt: 1,
    "& .MuiMenuItem-root": {
      padding: "12px 20px",
      margin: "4px 8px",
      borderRadius: "8px",
      transition: "all 0.2s ease-in-out",
      color: colors.gray[100],
      fontWeight: 600,
      fontSize: "14px",
      "&:hover": {
        backgroundColor:
          theme.palette.mode === "dark"
            ? "rgba(255, 255, 255, 0.1)"
            : "rgba(0, 0, 0, 0.04)",
      },
      "&:active": { transform: "scale(0.95)" },
      "&.Mui-selected": {
        backgroundColor:
          theme.palette.mode === "dark"
            ? "rgba(255, 255, 255, 0.15)"
            : "rgba(0, 0, 0, 0.08)",
        color: colors.blueAccent[500],
      },
      "&.Mui-selected:hover": {
        backgroundColor:
          theme.palette.mode === "dark"
            ? "rgba(255, 255, 255, 0.2)"
            : "rgba(0, 0, 0, 0.12)",
      },
    },
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const v = validate();
    if (v) {
      setError(v);
      setSnackbarOpen(true);
      return;
    }

    setSubmitting(true);
    setError(null);

    const payload = {
      amount: Number(amount),
      method,
      notes: note || null,
      receiptNumber: receiptNumber || null,
      paidAt: new Date().toISOString(),
      status: "PAID",
    };

    try {
      const res = await logInstallmentPayment(salesId, payload);
      const created = res?.data;

      setTimeout(() => {
        if (created?.dueDate) {
          setNextDueDate(created.dueDate);
        }

        setSuccessMsg("Payment logged successfully.");
        setSnackbarOpen(true);
        setAmount("");
        setNote("");
        setMethod("CASH");
        setReceiptNumber("");
        setSubmitting(false);

        if (typeof onPaymentLogged === "function") {
          try {
            onPaymentLogged(created);
          } catch (err) {
            console.warn("onPaymentLogged callback failed", err);
          }
        }
      }, 1000);
    } catch (err) {
      console.error("Failed to log installment payment:", err);
      const msg =
        err?.response?.data?.message ||
        err?.response?.data ||
        err?.message ||
        "Failed to log payment. Please try again.";
      setError(String(msg));
      setSnackbarOpen(true);
      setSubmitting(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
      <Box display="grid" gap="16px" gridTemplateColumns="repeat(2, 1fr)">
        <TextField
          fullWidth
          variant="filled"
          label="Amount"
          name="amount"
          type="number"
          inputProps={{ min: 0, step: "0.01" }}
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          required
          sx={{ gridColumn: "span 2", ...inputStyleProps }}
          InputProps={{
            disableUnderline: true,
            style: { borderRadius: "8px", fontSize: "0.95rem" },
          }}
          InputLabelProps={{ style: { fontSize: "0.95rem" } }}
        />

        <TextField
          select
          fullWidth
          variant="filled"
          label="Method"
          name="method"
          value={method}
          onChange={(e) => setMethod(e.target.value)}
          sx={{
            gridColumn: { xs: "span 2", sm: "span 1" },
            ...inputStyleProps,
          }}
          InputProps={{
            disableUnderline: true,
            style: { borderRadius: "8px", fontSize: "0.95rem" },
          }}
          InputLabelProps={{ style: { fontSize: "0.95rem" } }}
          SelectProps={{
            MenuProps: {
              TransitionComponent: Fade,
              transitionDuration: 300,
              PaperProps: { sx: dropdownPaperStyle },
            },
          }}
        >
          <MenuItem value="CASH">Cash</MenuItem>
          <MenuItem value="BANK_TRANSFER">Bank Transfer</MenuItem>
          <MenuItem value="POS">POS</MenuItem>
          <MenuItem value="MOBILE">Mobile</MenuItem>
          <MenuItem value="CARD">Card</MenuItem>
        </TextField>

        <TextField
          fullWidth
          variant="filled"
          label="Receipt Number (optional)"
          name="receiptNumber"
          value={receiptNumber}
          onChange={(e) => setReceiptNumber(e.target.value)}
          sx={{
            gridColumn: { xs: "span 2", sm: "span 1" },
            ...inputStyleProps,
          }}
          InputProps={{
            disableUnderline: true,
            style: { borderRadius: "8px", fontSize: "0.95rem" },
          }}
          InputLabelProps={{ style: { fontSize: "0.95rem" } }}
        />

        {nextDueDate && (
          <TextField
            fullWidth
            variant="filled"
            label="Next Due Date"
            name="nextDueDate"
            value={new Date(nextDueDate).toLocaleDateString()}
            sx={{ gridColumn: "span 2", ...inputStyleProps }}
            InputProps={{
              disableUnderline: true,
              readOnly: true,
              style: { borderRadius: "8px", fontSize: "0.95rem" },
            }}
            InputLabelProps={{ style: { fontSize: "0.95rem" } }}
          />
        )}

        <TextField
          fullWidth
          variant="filled"
          label="Note (optional)"
          name="note"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          multiline
          rows={2}
          sx={{ gridColumn: "span 2", ...inputStyleProps }}
          InputProps={{
            disableUnderline: true,
            style: { borderRadius: "8px", fontSize: "0.95rem" },
          }}
          InputLabelProps={{ style: { fontSize: "0.95rem" } }}
        />
      </Box>

      <Divider
        sx={{
          my: 3,
          mx: { xs: -2, sm: -3 },
          borderColor:
            theme.palette.mode === "dark"
              ? "rgba(255, 255, 255, 0.1)"
              : "rgba(0, 0, 0, 0.08)",
        }}
      />

      <Box
        display="flex"
        flexDirection={{ xs: "column-reverse", sm: "row" }}
        justifyContent="flex-end"
        gap={2}
      >
        {onClose && (
          <Button
            onClick={onClose}
            type="button"
            disabled={submitting}
            variant="outlined"
            sx={{
              color: colors.gray[100],
              borderColor: colors.gray[500],
              textTransform: "none",
              fontWeight: "600",
              px: { xs: 2, sm: 4 },
              py: { xs: 1.2, sm: 1.2 },
              borderRadius: "8px",
              width: { xs: "100%", sm: "auto" },
              transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
              "&:hover": {
                borderColor: colors.gray[300],
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? "rgba(255,255,255,0.05)"
                    : "rgba(0,0,0,0.04)",
              },
              "&:active": {
                transform: "scale(0.95)",
              },
            }}
          >
            Cancel
          </Button>
        )}
        <Button
          variant="contained"
          type="submit"
          disabled={
            submitting || (remainingBalance != null && remainingBalance <= 0)
          }
          sx={{
            backgroundColor: colors.blueAccent[600],
            color: "#ffffff",
            textTransform: "none",
            fontWeight: "600",
            px: { xs: 2, sm: 4 },
            py: { xs: 1.2, sm: 1.2 },
            borderRadius: "8px",
            width: { xs: "100%", sm: "auto" },
            boxShadow:
              theme.palette.mode === "dark"
                ? "0 4px 10px rgba(0,0,0,0.3)"
                : "0 4px 12px rgba(79, 70, 229, 0.2)",
            transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
            "&:hover": {
              backgroundColor: colors.blueAccent[500],
              boxShadow:
                theme.palette.mode === "dark"
                  ? "0 6px 14px rgba(0,0,0,0.4)"
                  : "0 6px 16px rgba(79, 70, 229, 0.3)",
              transform: "translateY(-1px)",
            },
            "&:active": {
              transform: "scale(0.95)",
              boxShadow: "none",
            },
            "&.Mui-disabled": {
              backgroundColor:
                theme.palette.mode === "dark"
                  ? "rgba(255, 255, 255, 0.12)"
                  : "rgba(0, 0, 0, 0.12)",
              color:
                theme.palette.mode === "dark"
                  ? "rgba(255, 255, 255, 0.3)"
                  : "rgba(0, 0, 0, 0.26)",
            },
          }}
        >
          {submitting ? (
            <>
              Logging&nbsp;
              <CircularProgress
                size={18}
                sx={{ color: "inherit", ml: "8px" }}
              />
            </>
          ) : (
            "Log Payment"
          )}
        </Button>
      </Box>

      <Portal>
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={3500}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          TransitionComponent={SlideTransition}
          sx={{ mt: 8, zIndex: 9999 }}
        >
          {successMsg ? (
            <Alert
              onClose={handleCloseSnackbar}
              severity="success"
              variant="filled"
              sx={{
                width: "100%",
                backgroundColor: colors.greenAccent[700],
                color: "#fff",
                fontWeight: "600",
                borderRadius: "8px",
                boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
              }}
            >
              {successMsg}
            </Alert>
          ) : error ? (
            <Alert
              onClose={handleCloseSnackbar}
              severity="error"
              variant="filled"
              sx={{
                width: "100%",
                backgroundColor: colors.redAccent[700],
                color: "#fff",
                fontWeight: "600",
                borderRadius: "8px",
                boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
              }}
            >
              {error}
            </Alert>
          ) : (
            <div />
          )}
        </Snackbar>
      </Portal>
    </Box>
  );
};

export default LogPayments;
